import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl,
  InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import {
  CheckCircle, Error as ErrorIcon, OpenInNew, PlayArrow, Refresh,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { megaphoneMumAPI } from '../../services/api';

/**
 * MUM (Megaphone private-API ETL) setup + status.
 *
 * What this is: Megaphone's public REST API doesn't expose per-day
 * downloads or Spotify streams. The CMS web UI does, via private
 * endpoints. We auth as a logged-in user via session cookies, scrape
 * the same data, and persist it for near-real-time analytics.
 *
 * Two halves of the page:
 *   1. Status snapshot per site (cookies present? token expiry? last
 *      run? row count?) + scheduler health
 *   2. Cookie capture form — paste session_id + cms_web_token from
 *      DevTools, server probes them with a real Megaphone request,
 *      saves on PASS.
 *
 * Re-capture is needed roughly weekly (the session_id eventually
 * rotates server-side even though the JWT auto-refreshes daily).
 */

interface KeepaliveSite {
  context_loaded: boolean | null;
  last_kept_alive: string | null;
  last_kept_alive_age_seconds: number | null;
  keepalive_last_error: string | null;
  auth_cookie_count: number | null;
  has_live_cookies: boolean;
  live_token_expires_at: string | null;
  live_token_expires_in_hours: number | null;
  live_fetch_error: string | null;
}

interface SiteStatus {
  site_slug: string;
  organization_id: string | null;
  configured: boolean;
  token_source: 'keepalive' | 'db' | 'none';
  token_expires_at: string | null;
  token_expires_in_hours: number | null;
  db_token_expires_at: string | null;
  db_token_expires_in_hours: number | null;
  keepalive: KeepaliveSite | null;
  last_run_at: string | null;
  last_error: string | null;
  metric_row_count: number;
  latest_metric_date: string | null;
  latest_fetch: string | null;
}

interface KeepaliveSummary {
  url: string | null;
  reachable: boolean;
  error: string | null;
  started_at: string | null;
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '—';

/**
 * Compact 7-day delivery sparkline. Fetches its own data; tiny enough
 * to live inline in each site's status card without N+1 concerns
 * (one query per site visible on the tab).
 */
const TimelineSparkline: React.FC<{ site: string }> = ({ site }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['mum-timeline', site, 7],
    queryFn: () => megaphoneMumAPI.timeline(site, 7),
    refetchInterval: 60_000,
  });
  if (isLoading) return <Box sx={{ height: 36, opacity: 0.4 }} />;
  const rows = (data?.timeline ?? []) as Array<{
    metric_date: string; downloads: number; streams: number; delivery: number;
  }>;
  if (rows.length === 0) {
    return (
      <Typography variant="caption" sx={{ opacity: 0.5 }}>
        no metrics yet — first MUM tick will populate
      </Typography>
    );
  }
  const total7d = rows.reduce((sum, r) => sum + (r.delivery || 0), 0);
  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.7 }}>
          last 7d delivery: <strong style={{ color: '#00ff41' }}>
            {total7d.toLocaleString()}
          </strong>
        </Typography>
      </Stack>
      <Box sx={{ width: '100%', height: 60 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 2, right: 8, bottom: 2, left: 0 }}>
            <XAxis dataKey="metric_date" hide />
            <YAxis hide />
            <Tooltip
              contentStyle={{
                background: '#0a0a0a', border: '1px solid #1e3a1e', fontSize: 12,
              }}
              labelStyle={{ color: '#00ff41' }}
              formatter={(v: number, name: string) => [v?.toLocaleString(), name]}
            />
            <Line type="monotone" dataKey="downloads" stroke="#00ff41"
                  strokeWidth={1.5} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="streams" stroke="#1DB954"
                  strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

const MumSetupTab: React.FC<{ mumDeep?: { site: string; nonce: number } | null }> = ({ mumDeep }) => {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['mum-status'],
    queryFn: megaphoneMumAPI.status,
    refetchInterval: 30_000,  // keep fresh while user is on the page
  });

  const [site, setSite] = useState<'spoutlore' | 'mallbrats'>('spoutlore');
  const formRef = useRef<HTMLDivElement>(null);
  // Deep-link from the dead-session banner: preselect the dead show + scroll
  // straight to the paste form so the user lands exactly where they paste.
  useEffect(() => {
    if (mumDeep?.site === 'spoutlore' || mumDeep?.site === 'mallbrats') {
      setSite(mumDeep.site);
    }
    if (mumDeep) {
      const t = setTimeout(
        () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      return () => clearTimeout(t);
    }
  }, [mumDeep?.nonce]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [sessionId, setSessionId] = useState('');
  const [token, setToken] = useState('');
  const [setupResult, setSetupResult] = useState<any | null>(null);
  const [runResult, setRunResult] = useState<any | null>(null);

  const setupMut = useMutation({
    mutationFn: megaphoneMumAPI.setupCookies,
    onSuccess: (r) => {
      setSetupResult({ ok: true, ...r });
      setSessionId('');
      setToken('');
      qc.invalidateQueries({ queryKey: ['mum-status'] });
    },
    onError: (e: any) => {
      setSetupResult({
        ok: false,
        error: e?.response?.data?.detail || e?.message || 'unknown error',
      });
    },
  });

  const runMut = useMutation({
    mutationFn: (s?: string) => megaphoneMumAPI.runNow(s),
    onSuccess: (r) => {
      setRunResult(r);
      qc.invalidateQueries({ queryKey: ['mum-status'] });
    },
    onError: (e: any) => {
      setRunResult({
        exit_code: -1,
        stderr: e?.response?.data?.detail || e?.message || 'unknown error',
      });
    },
  });

  const reloginMut = useMutation({
    mutationFn: (s?: string) => megaphoneMumAPI.reloginNow(s),
    onSuccess: (r) => {
      setRunResult({
        ...r,
        stdout: `=== HEADLESS RE-LOGIN ===\n${r.stdout || ''}`,
      });
      qc.invalidateQueries({ queryKey: ['mum-status'] });
    },
    onError: (e: any) => {
      setRunResult({
        exit_code: -1,
        stderr: e?.response?.data?.detail || e?.message || 'unknown error',
      });
    },
  });

  const sites: SiteStatus[] = data?.sites ?? [];
  const schedule = data?.schedule;
  const keepalive: KeepaliveSummary | undefined = data?.keepalive;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontFamily: 'monospace', mb: 1 }}>
        // MUM ETL :: NEAR-REAL-TIME MEGAPHONE METRICS
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        Pulls per-episode per-day downloads + Spotify streams from Megaphone's
        private CMS API every 30 min. Auth via browser session cookies — these
        rotate server-side every ~7 days, so plan on re-capturing weekly.
      </Typography>

      {/* ── Status snapshot ───────────────────────── */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load status: {(error as any)?.message}</Alert>
      ) : (
        <>
          {/* Keepalive worker banner — surfaces the live source of truth */}
          {keepalive && (
            <Alert
              severity={keepalive.reachable ? 'success' : 'warning'}
              icon={false}
              sx={{
                mb: 2, fontFamily: 'monospace', fontSize: '0.85em',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Box>
                  <strong>// KEEPALIVE WORKER</strong>{' '}
                  <span style={{ opacity: 0.7 }}>{keepalive.url || '(not configured)'}</span>
                </Box>
                <Chip
                  size="small"
                  label={keepalive.reachable ? 'reachable' : 'unreachable'}
                  color={keepalive.reachable ? 'success' : 'warning'}
                  sx={{ fontFamily: 'monospace' }}
                />
                {keepalive.started_at && (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    up since {new Date(keepalive.started_at).toLocaleString()}
                  </Typography>
                )}
                {keepalive.error && (
                  <Typography variant="caption" sx={{ color: '#ff9000' }}>
                    {keepalive.error}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {sites.map((s) => {
              const ka = s.keepalive;
              const expH = s.token_expires_in_hours;
              const expColor =
                expH == null            ? 'text.secondary'
                : expH < 0              ? '#ff4040'
                : expH < 6              ? '#ff9000'
                                        : '#00ff41';
              const sourceLabel =
                s.token_source === 'keepalive' ? 'LIVE'
                : s.token_source === 'db'      ? 'STALE DB'
                                               : 'NONE';
              const sourceColor =
                s.token_source === 'keepalive' ? '#00ff41'
                : s.token_source === 'db'      ? '#ff9000'
                                               : '#888';
              return (
              <Paper key={s.site_slug} sx={{ p: 2 }} variant="outlined">
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={s.site_slug.toUpperCase()}
                    color={s.configured ? 'success' : 'default'}
                    icon={s.configured ? <CheckCircle /> : <ErrorIcon />}
                    sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                  />
                  {!s.configured ? (
                    <Typography variant="body2" color="text.secondary">
                      No cookies captured yet — paste them below.
                    </Typography>
                  ) : (
                    <>
                      <Chip
                        size="small"
                        label={sourceLabel}
                        sx={{
                          fontFamily: 'monospace', fontWeight: 700, fontSize: '0.7em',
                          bgcolor: 'transparent', color: sourceColor,
                          border: '1px solid', borderColor: sourceColor,
                        }}
                        title={
                          s.token_source === 'keepalive'
                            ? 'JWT expiry read from live keepalive worker cookies'
                            : s.token_source === 'db'
                            ? 'Keepalive worker has no live cookies; showing JWT expiry from last manual paste in DB (stale)'
                            : 'No cookies anywhere — interactive login needed on box'
                        }
                      />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        token expires in{' '}
                        <strong style={{ color: expColor }}>
                          {expH != null ? `${expH}h` : '?'}
                        </strong>
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        last run: {fmtDate(s.last_run_at)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        rows: <strong>{s.metric_row_count.toLocaleString()}</strong>
                        {s.latest_metric_date && ` (latest ${s.latest_metric_date})`}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size="small"
                        startIcon={<PlayArrow />}
                        onClick={() => runMut.mutate(s.site_slug)}
                        disabled={runMut.isPending}
                      >
                        Run now
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => reloginMut.mutate(s.site_slug)}
                        disabled={reloginMut.isPending}
                        title="Headless-browser re-login to refresh cookies (requires MUM_MEGAPHONE_EMAIL/PASSWORD env vars)"
                      >
                        Re-login
                      </Button>
                    </>
                  )}
                </Stack>

                {/* Keepalive per-site sub-row */}
                {ka && (
                  <Stack direction="row" spacing={2} sx={{ mt: 1, opacity: 0.8 }} flexWrap="wrap">
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      context:{' '}
                      <strong style={{ color: ka.context_loaded ? '#00ff41' : '#ff4040' }}>
                        {ka.context_loaded ? 'loaded' : 'missing'}
                      </strong>
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      auth cookies in jar:{' '}
                      <strong style={{
                        color: (ka.auth_cookie_count ?? 0) === 2 ? '#00ff41' : '#ff9000',
                      }}>
                        {ka.auth_cookie_count == null ? '?' : `${ka.auth_cookie_count}/2`}
                      </strong>
                    </Typography>
                    {ka.last_kept_alive_age_seconds != null && (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        last ping: {Math.round(ka.last_kept_alive_age_seconds)}s ago
                      </Typography>
                    )}
                    {ka.keepalive_last_error && (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#ff9000' }}>
                        ⚠ {ka.keepalive_last_error}
                      </Typography>
                    )}
                    {(ka.auth_cookie_count != null && ka.auth_cookie_count < 2) && (
                      <Button
                        size="small"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7em' }}
                        onClick={async () => {
                          try {
                            const r = await megaphoneMumAPI.cookiesDebug(s.site_slug);
                            setRunResult({
                              exit_code: 0,
                              stdout: '=== /cookies-debug ===\n' + JSON.stringify(r, null, 2),
                            });
                          } catch (e: any) {
                            setRunResult({
                              exit_code: -1,
                              stderr: e?.response?.data?.detail || e?.message,
                            });
                          }
                        }}
                      >
                        Cookie diagnostic
                      </Button>
                    )}
                  </Stack>
                )}

                {s.last_error && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    Last error: <code>{s.last_error}</code>
                  </Alert>
                )}
                {s.configured && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <TimelineSparkline site={s.site_slug} />
                  </Box>
                )}
              </Paper>
              );
            })}
          </Stack>

          {schedule && (
            <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  Cron <code>megaphone_mum</code>
                </Typography>
                <Chip
                  label={schedule.enabled ? 'ENABLED' : 'DISABLED'}
                  size="small"
                  color={schedule.enabled ? 'success' : 'default'}
                />
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {schedule.cron_string}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  last success: {fmtDate(schedule.last_success_at)}
                </Typography>
                {schedule.consecutive_failures > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={`${schedule.consecutive_failures} consecutive failures`}
                  />
                )}
              </Stack>
              {!schedule.enabled && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                  Cron is disabled by default until at least one site is configured.
                  After your first cookie capture below probes OK, flip{' '}
                  <code>public.etl_schedule.enabled = true</code> for{' '}
                  <code>megaphone_mum</code>.
                </Typography>
              )}
            </Paper>
          )}
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ── Cookie capture form ───────────────────── */}
      <Typography variant="h6" sx={{ fontFamily: 'monospace', mb: 1 }}>
        // CAPTURE COOKIES
      </Typography>

      <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,0,0,0.06)', borderColor: 'error.main' }} variant="outlined">
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main', mb: 0.5 }}>
          ⚠ DON'T click "Sign Out" in cms.megaphone.fm after capturing.
        </Typography>
        <Typography variant="caption">
          Signing out kills the session server-side, which also kills the
          cron's access. <strong>Closing the tab is fine</strong> — just don't
          click Sign Out. (Clearing cookies for cms.megaphone.fm has the same effect.)
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,255,255,0.02)' }} variant="outlined">
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Capture procedure (~30 sec):</strong>
        </Typography>
        <Box component="ol" sx={{ pl: 3, mt: 0, mb: 1, fontSize: '0.95em' }}>
          <li>
            Open{' '}
            <a href="https://cms.megaphone.fm" target="_blank" rel="noreferrer">
              cms.megaphone.fm <OpenInNew fontSize="inherit" />
            </a>
            {' '}→ log in (Spotify SSO + captcha).
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Regular browser is fine. Private/incognito only helps as a
              guard against accidentally clicking Sign Out later — not required.
            </Typography>
          </li>
          <li>
            Open DevTools (Cmd-Opt-I) → <strong>Application</strong> tab → Cookies →{' '}
            <code>https://cms.megaphone.fm</code>
          </li>
          <li>
            Find and copy these TWO cookie values:
            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
              <li><code>_megaphone_session_id</code> (32 hex chars)</li>
              <li><code>_megaphone_cms_web_token</code> (long JWT starting <code>eyJ</code>)</li>
            </Box>
          </li>
          <li>Paste below + select which show this account has access to + submit.</li>
          <li><strong>Close the cms.megaphone.fm tab.</strong> Don't sign out.</li>
        </Box>
      </Paper>

      <Stack spacing={2} ref={formRef}>
        <FormControl size="small" sx={{ maxWidth: 200 }}>
          <InputLabel>Show</InputLabel>
          <Select
            label="Show"
            value={site}
            onChange={(e) => setSite(e.target.value as any)}
          >
            <MenuItem value="spoutlore">Spout Lore</MenuItem>
            <MenuItem value="mallbrats">Mall Brats</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="_megaphone_session_id"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value.trim())}
          fullWidth
          size="small"
          autoComplete="off"
          placeholder="e.g. f4a7f516840f19045d8057e8a8462b09"
          helperText="32-char hex string from DevTools Application → Cookies"
        />

        <TextField
          label="_megaphone_cms_web_token"
          value={token}
          onChange={(e) => setToken(e.target.value.trim())}
          fullWidth
          size="small"
          multiline
          rows={3}
          autoComplete="off"
          placeholder="eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJkYXRhI..."
          helperText="Long JWT (~250 chars). Server probes it with a real Megaphone request before saving."
        />

        <Box>
          <Button
            variant="contained"
            color="primary"
            disabled={!sessionId || !token || setupMut.isPending}
            onClick={() => setupMut.mutate({
              site_slug: site,
              session_id: sessionId,
              cms_web_token: token,
            })}
            startIcon={setupMut.isPending ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            Save & probe cookies for {site === 'spoutlore' ? 'Spout Lore' : 'Mall Brats'}
          </Button>
        </Box>

        {setupResult && (
          <Alert severity={setupResult.ok ? 'success' : 'error'}>
            {setupResult.ok ? (
              <>
                <strong>✓ Cookies saved + probed OK.</strong>{' '}
                Token expires in {setupResult.token_expires_in_hours}h.{' '}
                CSRF extracted: {setupResult.csrf_extracted ? 'yes' : 'no'}.
                <br />
                <em>{setupResult.next_step}</em>
              </>
            ) : (
              <>
                <strong>✗ Setup failed:</strong> {setupResult.error}
              </>
            )}
          </Alert>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />

      {/* ── Manual run output ─────────────────────── */}
      {runResult && (
        <Paper sx={{ p: 2 }} variant="outlined">
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
            Run-now result (exit_code = <code>{runResult.exit_code}</code>):
          </Typography>
          <Box
            component="pre"
            sx={{
              fontSize: '0.75em', fontFamily: 'monospace',
              bgcolor: 'rgba(0,0,0,0.3)', p: 1.5, borderRadius: 0.5,
              maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap',
              wordBreak: 'break-all', m: 0,
            }}
          >
            {runResult.stdout || ''}
            {runResult.stderr ? `\n--- stderr ---\n${runResult.stderr}` : ''}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MumSetupTab;
